import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema, caseStudySchema } from "../lib/seo";

const StFrancisCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/case-studies/st-francis-donor-event")}
        schema={[
          caseStudySchema("/case-studies/st-francis-donor-event"),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: "St. Francis Donor Event", path: "/case-studies/st-francis-donor-event" },
        ]),
        ]}
      />
      {/* Page Title Bar */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <Link
                to="/case-studies"
                className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
                data-testid="back-to-case-studies-link"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Case Studies
              </Link>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                St. Francis: Non-profit executes a Successful Donor Event
              </h1>
            </div>
            <div className="lg:col-span-3">
              <div
                className="w-full h-72 md:h-96 rounded-lg bg-cover bg-center shadow-2xl"
                style={{
                  backgroundImage:
                    "url('/images/case-studies/st-francis.jpg')",
                }}
                role="img"
                aria-label="St. Francis Donor Event"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Overview</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              St. Francis School in Houston, Texas, was gearing up for its annual fundraising gala in April 2023 and had a clear vision – to make the entire event completely cashless. This ambitious goal required a reliable nfc cashless solution to streamline operations and enhance the guest experience.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Approach</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              WristCoin stepped in to provide a comprehensive solution. Recognizing the school's need for nearly 50 terminals, we offered a cost-effective rental option from our extensive stock of mPOS terminals. To further reduce costs, WristCoin provided generic branded wristbands at a reduced price. Guests had the convenience of pre-purchasing these wristbands during gala registration.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              As enthusiasm for the event grew, and the guest list expanded, WristCoin proved its flexibility. We effortlessly delivered additional wristbands as needed, thanks to our readily available stock of generic branded wristbands. To ensure that going cashless was economically viable for the school, a registered not-for-profit, we configured the system so that credit card processing charges were added to the customer's bill. Remarkably, donors were more than willing to cover these charges.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Solution</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              The implementation of WristCoin's solution at St. Francis School's fundraising gala yielded impressive results. The event became entirely cashless, streamlining transactions and enhancing security. The cost-effective rental of mPOS terminals and the availability of generic branded wristbands made the transition to a cashless system both affordable and efficient.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Guests embraced the convenience of pre-purchasing wristbands, contributing to an increase in gala attendance. Donors enthusiastically covered credit card processing charges, demonstrating their support for the school's mission.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              A video created by the school's organizing committee beautifully captured the success and ease of using the wristbands, further promoting the benefits of the cashless system.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Outcome</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              St. Francis School's partnership with WristCoin not only achieved the goal of a completely cashless gala but also reduced costs and increased guest participation. The success story highlights how innovative solutions can drive efficiency and support not-for-profit initiatives. In summary, WristCoin's solution brought added convenience and financial support to St. Francis School's fundraising efforts.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default StFrancisCaseStudy;
